import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import Table from '../common/Table';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';

const PGInvoicesTab = ({ pgId, onShowAddPayment, onShowAddAdjustment }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  useEffect(() => {
    if (pgId) {
      fetchInvoices();
    }
  }, [pgId]);

  const handleAddPayment = async (invoiceId) => {
    try {
      // Fetch invoice details to get PG information
      const invoiceResponse = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (invoiceResponse?.data) {
        const invoice = invoiceResponse.data;
        
        // Fetch PG details
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          // Call parent component's handler
          onShowAddPayment(invoice, pgResponse.data);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else {
        toastError('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toastError('Failed to load invoice data');
    }
  };

  const handleAddAdjustment = async (invoiceId) => {
    try {
      const invoiceResponse = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (invoiceResponse?.data) {
        const invoice = invoiceResponse.data;
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          onShowAddAdjustment(invoice, pgResponse.data);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else {
        toastError('Failed to load invoice details');
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toastError('Failed to load invoice data');
    }
  };

  const fetchInvoices = async () => {
    if (invoicesLoaded) return;
    
    try {
      setInvoicesLoading(true);
      setInvoicesError('');
      
      const response = await apiGet(`/invoicing/invoices?pg_id=${pgId}&limit=1000`);
      // Handle new response structure {invoices: [...], totalInvoicesAmount: number}
      const responseData = response?.data || [];
      const invoicesData = Array.isArray(responseData) ? responseData : (responseData.invoices || []);
      
      setInvoices(invoicesData);
      
      // Calculate summary
      const totalInvoices = invoicesData.length;
      const draftInvoices = invoicesData.filter(inv => inv.status === 'draft').length;
      const paidInvoices = invoicesData.filter(inv => inv.status === 'paid').length;
      const unpaidInvoices = invoicesData.filter(inv => ['sent', 'overdue', 'partially_paid'].includes(inv.status)).length;
      const totalUnpaidAmount = invoicesData
        .filter(inv => ['sent', 'overdue', 'partially_paid'].includes(inv.status))
        .reduce((sum, inv) => sum + (inv.amount_remaining || 0), 0);
      
      setInvoiceSummary({
        totalInvoices,
        draftInvoices,
        paidInvoices,
        unpaidInvoices,
        totalUnpaidAmount
      });
      
      setInvoicesLoaded(true);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoicesError('Failed to load invoice information');
      toastError('Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const invoiceColumns = [
    {
      Header: 'Invoice #',
      accessor: 'invoice_number'
    },
    {
      Header: 'Date',
      accessor: 'invoice_date'
    },
    {
      Header: 'Due Date',
      accessor: 'due_date'
    },
    {
      Header: 'Items',
      accessor: 'items'
    },
    {
      Header: 'Total Amount',
      accessor: 'total_amount',
      Cell: ({ row }) => `₹${row.total_amount?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Paid',
      accessor: 'amount_paid',
      Cell: ({ row }) => `₹${row.amount_paid?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Amount Remaining',
      accessor: 'amount_remaining',
      Cell: ({ row }) => `₹${row.amount_remaining?.toFixed(2) || '0.00'}`
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        const statusColors = {
          draft: 'bg-gray-100 text-gray-800',
          sent: 'bg-blue-100 text-blue-800',
          partially_paid: 'bg-yellow-100 text-yellow-800',
          overdue: 'bg-red-100 text-red-800',
          paid: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800'
        };
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[row.status?.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
            {row.status?.charAt(0).toUpperCase() + row.status?.slice(1).replace('_', ' ') || 'Draft'}
          </span>
        );
      }
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const status = row.status?.toLowerCase();
        const showAddPayment = status === 'sent' || status === 'overdue';
        const showAddAdjustment = status === 'sent' || status === 'partially_paid';
        
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <RTButton
              type="link"
              size="small"
              onClick={() => navigate(`/payments/invoices/${row.id}/details`)}
            >
              View
            </RTButton>
            {showAddPayment && (
              <RTButton
                type="link"
                size="small"
                onClick={() => handleAddPayment(row.id)}
              >
                Add Payment
              </RTButton>
            )}
            {showAddAdjustment && (
              <RTButton
                type="link"
                size="small"
                onClick={() => handleAddAdjustment(row.id)}
              >
                Add Adjustment
              </RTButton>
            )}
          </div>
        );
      }
    }
  ];

  const prepareInvoiceTableData = () => {
    return invoices.map(invoice => ({
      id: invoice.invoice_id, // Use 'id' instead of 'invoice_id'
      invoice_number: invoice.invoice_number,
      invoice_date: formatDateToDDMMYYYY(invoice.invoice_date),
      due_date: formatDateToDDMMYYYY(invoice.due_date),
      items: invoice.items_text || '-',
      total_amount: invoice.total_amount,
      amount_paid: invoice.amount_paid,
      amount_remaining: invoice.amount_remaining,
      status: invoice.status,
      invoice: invoice
    }));
  };

  return (
    <div style={{ padding: '20px' }}>
      {invoicesLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <RTSpinner message="Loading invoices..." />
        </div>
      ) : invoicesError ? (
        <div style={{ textAlign: 'center', color: '#dc3545', padding: '40px' }}>
          <p>{invoicesError}</p>
          <RTButton variant="primary" onClick={() => { setInvoicesLoaded(false); fetchInvoices(); }}>
            Retry
          </RTButton>
        </div>
      ) : (
        <>
          {/* Invoice Summary */}
          {invoiceSummary && (
            <div className="rt-summary-section">
              <div className="rt-summary-table">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ color: '#153C7F', margin: 0 }}>Invoice Summary</h4>
                  <RTButton
                    type="link"
                    variant="primary"
                    onClick={() => navigate('/payments/invoices/create', { state: { pgId } })}
                  >
                    + Add Invoice
                  </RTButton>
                </div>
                <div>
                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Invoices:</label>
                      <span>{invoiceSummary.totalInvoices}</span>
                    </div>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Paid Invoices:</label>
                      <span>{invoiceSummary.paidInvoices}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Draft Invoices:</label>
                      <span>{invoiceSummary.draftInvoices}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Unpaid Invoices:</label>
                      <span>{invoiceSummary.unpaidInvoices}</span>
                    </div>
                    <div style={{ flex: 1, marginRight: '20px' }}>
                      <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Total Unpaid Amount:</label>
                      <span>₹{invoiceSummary.totalUnpaidAmount?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      {/* Empty space for alignment */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoice Table */}
          {invoices.length > 0 ? (
            <Table
              title={`Invoices (${invoices.length} total)`}
              columns={invoiceColumns}
              data={prepareInvoiceTableData()}
              showSearch={true}
              exportable={true}
              showPagination={true}
              pageSize={20}
              noDataText="No invoices found"
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No invoices found for this paying guest.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PGInvoicesTab;
